import Styles from './styles.module.css';
import React, { useState, useEffect } from 'react'
import html2canvas from 'html2canvas';
import { Timestamp, doc as firestoreDoc, getFirestore, onSnapshot } from "firebase/firestore";
import { Applicant, docData } from 'src/type';

type FirestoreDate = Timestamp | Date;

const { jsPDF } = require('jspdf');

type DetailsResumeProps = {
  documentData: docData;
  applicant: Applicant;
};

const DetailsResume: React.FC<DetailsResumeProps> = ({ documentData, applicant }) => {

  const [ str, setStr ] = useState('');
  const [ time, setTime ] = useState<number>(0);

  // 履歴書本体(基本情報〜志望動機・自己PR)は、DOMをhtml2canvasでキャプチャして
  // 都度組み立てる代わりに、Cloud Functions(generateResumePdf)が事前生成した
  // PDF/プレビュー画像をそのまま表示する(見た目が実際の提出物と完全に一致し、
  // 表示側の端末や解像度にも影響されないため)。
  const [ generatedPdfUrl, setGeneratedPdfUrl ] = useState('');
  const [ previewUrls, setPreviewUrls ] = useState<string[]>([]);

  useEffect(() => {
    if (!applicant?.id) return;
    const db = getFirestore();
    const unsubscribe = onSnapshot(firestoreDoc(db, 'applicants', applicant.id), (snap) => {
      const generated = snap.data()?.generatedResumePdf;
      setGeneratedPdfUrl(generated?.url ?? '');
      setPreviewUrls(Array.isArray(generated?.previewUrls) ? generated.previewUrls : []);
    });
    return () => unsubscribe();
  }, [applicant?.id]);

  // 元々作成済みのPDF(Cloud Functionsが生成しStorageに保存したもの)をそのまま
  // ダウンロードする。html2canvasでの再生成は行わない。
  const downloadGeneratedPdf = async () => {
    if (!generatedPdfUrl) return;
    try {
      const res = await fetch(generatedPdfUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `履歴書（${applicant?.basicInfo.name}様）.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('resume pdf download failed:', error);
    }
  };

  const mmToPx = (mm: number) => Math.round((mm * 96) / 25.4);

  // 例：高解像度で切る
  const PHOTO_W = mmToPx(22.5) * 4;
  const PHOTO_H = mmToPx(30) * 4;

  useEffect(() => {
    if (!applicant?.basicInfo?.idImageUrl) return;

    const timeInit = new Date().getTime();
    setTime(timeInit);

    const large_image = `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/resume%2F${applicant.basicInfo.idImageUrl}?alt=media&token=${timeInit}`;

    (async () => {
      const base64 = await new Promise<string>((resolve) => {
        toBase64Url(large_image, resolve);
      });

      const cropped = await cropImageToCover(
        base64,
        PHOTO_W,
        PHOTO_H
      );

      setStr(cropped); // ← ここが超重要
    })();
  }, [applicant?.id]);
  
  function toBase64Url(url: string, callback: (base64Url: string) => void) {
    var xhr = new XMLHttpRequest();
    xhr.onload = function() {
      var reader = new FileReader();
      reader.onloadend = function() {
        callback(reader.result as string);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  }

  const pdf = async () => {
    const doc = new jsPDF({
      orientation: 'p',
      format: 'a4',
      unit: 'mm',
    });

    const elements = ['output1', 'output2'];
    // 見本PDFの帳票幅(約183mm)は、A4(210mm)から左右13.5mmずつの余白を
    // 引いた幅にほぼ一致する。座標を決め打ちするのではなく、DOM側の寸法
    // (.box: 410px×620px)を正しく保ったうえで、通常の余白基準フィット+
    // 上下中央寄せで配置する。
    const margin = 13.5;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let i = 0; i < elements.length; i++) {
      const elem = document.getElementById(elements[i]);
      if (!elem) continue;

      const canvas = await html2canvas(elem, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');

      // px → mm（96dpi基準）
      const pxToMm = (px: number) => (px * 25.4) / 96;

      const imgWidthMm = pxToMm(canvas.width);
      const imgHeightMm = pxToMm(canvas.height);

      // *0.995: 罫線がページ端/画像端でクリップされないよう、わずかに余裕を持たせる
      const scale = Math.min(
        (pageWidth - margin * 2) / imgWidthMm,
        (pageHeight - margin * 2) / imgHeightMm
      ) * 0.995;

      const renderW = imgWidthMm * scale;
      const renderH = imgHeightMm * scale;

      if (i !== 0) {
        doc.addPage();
      }

      doc.addImage(
        imgData,
        'PNG',
        (pageWidth - renderW) / 2,
        margin,
        renderW,
        renderH
      );
    }

    doc.save(`履歴書（${applicant?.basicInfo.name}様）.pdf`);
  };




  const cropImageToCover = async (
    imgSrc: string,
    targetW: number,
    targetH: number
  ): Promise<string> => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imgSrc;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext('2d')!;
    const imgRatio = img.width / img.height;
    const targetRatio = targetW / targetH;

    let sx = 0,
      sy = 0,
      sw = img.width,
      sh = img.height;

    if (imgRatio > targetRatio) {
      // 横長 → 横を切る
      sw = img.height * targetRatio;
      sx = (img.width - sw) / 2;
    } else {
      // 縦長 → 縦を切る
      sh = img.width / targetRatio;
      sy = (img.height - sh) / 2;
    }

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
    return canvas.toDataURL('image/png');
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return "";

    // 数字以外を除去
    const digits = phone.replace(/\D/g, "");

    // 携帯（090 / 080 / 070）
    if (/^(070|080|090)\d{8}$/.test(digits)) {
      return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    }

    // 固定電話（市外局番 2〜4桁 想定）
    if (digits.length === 10) {
      return digits.replace(/(\d{2,4})(\d{2,4})(\d{4})/, "$1-$2-$3");
    }

    // その他はそのまま返す
    return phone;
  };
  
  return (
    <>
    {/* 履歴書本体(基本情報〜志望動機・自己PR)のDOM直接レンダリング(html2canvasキャプチャ対象)は
        一時的にコメントアウトし、Cloud Functions(generateResumePdf)が事前生成した
        プレビュー画像で表示するよう切り替える(下のdocumentData?.resume &&の分岐を参照)。
    {documentData?.resume ?
    <>
      <button className={Styles.save_btn} onClick={() => pdf()}>PDF書き出し</button>
      <div style={{height: "20px"}}></div>
      <div className={Styles.box} id='output1'>


        <div className={Styles.resume_1}>
          <div>
            <div style={{ height: "20px" }}></div>
            <div className={Styles.resume_title}>
              <h2>履歴書</h2>
              <h3>
                {(() => {
                  if (!applicant?.time) return null;
                  const date = applicant?.time instanceof Date ? applicant?.time : applicant?.time.toDate();
                  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
                })()} 現在
              </h3>
            </div>
            <table className={Styles.basicTable}>
              <tbody>
                <tr>
                  <td colSpan={2} className={Styles.basicFuriganaCell}>
                    <div style={{display: "flex"}}>
                      <h4 className={Styles.resume_hurigana}>フリガナ</h4>
                      <p className={Styles.resume_hurigana_contents}>{applicant?.basicInfo?.nameHurigana ?? ""}</p>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} className={Styles.basicNameCell}>
                    <div style={{display: "flex"}}>
                      <h4 className={Styles.resume_hurigana}>氏名</h4>
                      <p className={`${Styles.resume_hurigana_contents} ${Styles.nameValueCell}`}>{applicant?.basicInfo?.name ?? ""}</p>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className={Styles.basicBirthCell}>
                    {applicant?.basicInfo?.birthday?.year ?? ""}年{applicant?.basicInfo?.birthday?.month ?? ""}月{applicant?.basicInfo?.birthday?.day ?? ""}日　満 {applicant?.basicInfo?.age ?? ""}歳
                  </td>
                  <td className={Styles.basicSexCell}>
                    {applicant?.basicInfo?.sex ?? ""}・{applicant?.basicInfo?.bloodType ?? ""}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={Styles.photoFrame}>
            <img src={str} className={Styles.resume_img} />
          </div>

        </div>
        
        <table className={Styles.addressTable}>
          <tbody>
            <tr className={Styles.addressFuriganaRow}>
              <td className={Styles.addressLeftCell}>
                <div className={Styles.addressCellContent}>
                  <h4 className={Styles.resume_hurigana}>フリガナ</h4>
                  <p className={Styles.resume_hurigana_contents}>{applicant?.basicInfo?.address?.addressHurigana ?? ""}</p>
                </div>
              </td>
              <td className={Styles.addressRightCell}>
                <div className={Styles.addressCellContent}>
                  <h4 className={Styles.resume_hurigana}>電話　</h4>
                  <p className={Styles.resume_hurigana_contents}>{formatPhoneNumber(applicant?.basicInfo?.phone)}</p>
                </div>
              </td>
            </tr>
            <tr className={Styles.addressMainRow}>
              <td className={Styles.addressLeftCell}>
                <p className={Styles.resume_hurigana_contents} style={{ marginLeft: "10px" }}>現住所　〒 {applicant?.basicInfo?.address.postcode.substr(0,3)}-{applicant?.basicInfo?.address.postcode.substr(3,4)}</p>
                <p className={Styles.resume_hurigana_contents} style={{ marginLeft: "10px", fontSize: "10px" }}>{applicant?.basicInfo?.address.address}</p>
              </td>
              <td className={Styles.addressRightCell}>
                <div className={Styles.addressCellContent}>
                  <h4 className={Styles.resume_hurigana}>メール　</h4>
                  <p className={`${Styles.resume_hurigana_contents} ${Styles.mailText}`}>
                    {applicant?.basicInfo?.mail ?? ""}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className={Styles.historyTable}>
          <tbody>
            <tr>
              <td className={`${Styles.yearCell} ${Styles.historyHeaderCell}`}>年</td>
              <td className={`${Styles.monthCell} ${Styles.historyHeaderCell}`}>月</td>
              <td className={`${Styles.historyTextCell} ${Styles.historyHeaderCell}`}>学歴・職歴 / 資格取得・受賞歴</td>
            </tr>
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={`${Styles.historyTextCell} ${Styles.historyHeaderCell}`}>学歴・職歴</td>
            </tr>
            {([1, 2, 3, 4, 5, 6] as const).map((i) => {
              const key = `history${i}` as 'history1' | 'history2' | 'history3' | 'history4' | 'history5' | 'history6';
              const history = applicant?.histories?.[key];
              const hasText = history?.text !== '';
              return (
                <tr key={key}>
                  <td className={Styles.yearCell}>{hasText ? history?.years ?? '' : ''}</td>
                  <td className={Styles.monthCell}>{hasText ? history?.month ?? '' : ''}</td>
                  <td className={Styles.historyTextCell}>　{hasText ? history?.text ?? '' : ''}</td>
                </tr>
              );
            })}
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={Styles.historyTextCell} />
            </tr>
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={Styles.historyTextCell} />
            </tr>
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={`${Styles.historyTextCell} ${Styles.historyHeaderCell}`}>資格取得・受賞歴</td>
            </tr>
            {([1, 2, 3] as const).map((i) => {
              const key = `award${i}` as 'award1' | 'award2' | 'award3';
              const award = applicant?.awards?.[key];
              const hasText = award?.text !== '';
              return (
                <tr key={key}>
                  <td className={Styles.yearCell}>{hasText ? award?.years ?? '' : ''}</td>
                  <td className={Styles.monthCell}>{hasText ? award?.month ?? '' : ''}</td>
                  <td className={Styles.historyTextCell}>　{hasText ? award?.text ?? '' : ''}</td>
                </tr>
              );
            })}
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={Styles.historyTextCell} />
            </tr>
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={Styles.historyTextCell} />
            </tr>
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={Styles.historyTextCell} style={{ textAlign: 'right', paddingRight: '8px' }}>以上　</td>
            </tr>
            <tr>
              <td className={Styles.yearCell} />
              <td className={Styles.monthCell} />
              <td className={Styles.historyTextCell} />
            </tr>
          </tbody>
        </table>
      </div>

      <div className={Styles.box} id='output2'>

        <table className={Styles.socialTable}>
          <tbody>
            <tr>
              <td>　媒体名</td>
              <td>　アカウント名</td>
              <td>　フォロワー数</td>
            </tr>
            <tr>
              <td>　Instagram</td>
              <td>　{applicant?.basicInfo?.socialMedia.instagram}</td>
              <td>　{applicant?.basicInfo?.socialMedia.instagramFollower}人</td>
            </tr>
            <tr>
              <td>　Tiktok</td>
              <td>　{applicant?.basicInfo?.socialMedia.tiktok}</td>
              <td>　{applicant?.basicInfo?.socialMedia.tiktokFollower}人</td>
            </tr>
            <tr>
              <td>　X（Twitter）</td>
              <td>　{applicant?.basicInfo?.socialMedia.twitter}</td>
              <td>　{applicant?.basicInfo?.socialMedia.twitterFollower}人</td>
            </tr>
          </tbody>
        </table>

        <div className={Styles.resumeSectionGap} />

        <table className={Styles.motivationTable}>
          <tbody>
            <tr>
              <td className={Styles.sectionHeaderCell}>　志望動機</td>
            </tr>
            <tr>
              <td className={Styles.motivationBodyCell}>{applicant?.motivationPr.motivation}</td>
            </tr>
            <tr>
              <td className={Styles.sectionHeaderCell}>　自己PR</td>
            </tr>
            <tr>
              <td className={Styles.prBodyCell}>{applicant?.motivationPr.prText}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </> : <></> }
    */}

    {documentData?.resume ? (
      <>
        <button className={Styles.save_btn} onClick={() => downloadGeneratedPdf()} disabled={!generatedPdfUrl}>
          {generatedPdfUrl ? 'PDF書き出し' : 'PDF準備中...'}
        </button>
        <div style={{height: "20px"}}></div>
        {previewUrls.length === 0 ? (
          applicant?.basicInfo?.isCompleted ? (
            <p className={Styles.resume_hurigana_contents}>履歴書のプレビューを準備しています...</p>
          ) : (
            <p className={Styles.resume_hurigana_contents}>履歴書情報の登録がありません</p>
          )
        ) : (
          previewUrls.map((url, i) => (
            <div className={Styles.previewPage} key={url}>
              <img src={url} alt={`履歴書 ${i + 1}ページ目`} />
            </div>
          ))
        )}
      </>
    ) : <></>}

    {/* コーディネート写真・特記質問・その他提出書類も、履歴書本体と同じく
        Cloud Functions(generateResumePdf)が生成したプレビュー画像(previewUrls、
        3・4ページ目)側に含まれるようになったため、DOM直接レンダリングは
        コメントアウトする(二重表示を避けるため)。
    {documentData?.coordinate ?
      <div className={Styles.box}>
        <table className={Styles.preview_screen_table_3}>
          <tbody>
            <tr>
              <td rowSpan={2} style={{width: '150px', textAlign: 'left', fontSize: '12px', height: "180px", borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderBottom: "1px solid #1B1B1B"}}>
                <img style={{width: '150px', height: "180px", objectFit: "contain"}}
                  src={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/coordination%2Fresize_images%2F${applicant?.recruitId}_${applicant?.uid}_1_750x750?alt=media&token=${time}`} alt='' />
              </td>
              <td style={{width: '260px', textAlign: 'left', fontSize: '12px', height: "20px", borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B",}}>
                <p className={Styles.resume_hurigana_contents}>　{applicant?.coordinates?.coordinate1?.title ?? ""}</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '260px', textAlign: 'left', fontSize: '12px', height: "160px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", padding: "10px", borderBottom: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>{applicant?.coordinates?.coordinate1?.point ?? ""}</p>
              </td>
            </tr>
            <tr>
              <td rowSpan={2} style={{width: '150px', textAlign: 'left', fontSize: '12px', height: "180px", borderLeft: "1px solid #1B1B1B", borderBottom: "1px solid #1B1B1B"}}>
                <img style={{width: '150px', height: "180px", objectFit: "contain"}}
                  src={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/coordination%2Fresize_images%2F${applicant?.recruitId}_${applicant?.uid}_2_750x750?alt=media&token=${time}`} alt='' />
              </td>
              <td style={{width: '260px', textAlign: 'left', fontSize: '12px', height: "20px", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B",}}>
                <p className={Styles.resume_hurigana_contents}>　{applicant?.coordinates?.coordinate2?.title ?? ""}</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '260px', textAlign: 'left', fontSize: '12px', height: "160px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", padding: "10px", borderBottom: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>{applicant?.coordinates?.coordinate2?.point ?? ""}</p>
              </td>
            </tr>
            <tr>
              <td rowSpan={2} style={{width: '150px', textAlign: 'left', fontSize: '12px', height: "180px", borderLeft: "1px solid #1B1B1B", borderBottom: "1px solid #1B1B1B"}}>
                <img style={{width: '150px', height: "180px", objectFit: "contain"}}
                  src={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/coordination%2Fresize_images%2F${applicant?.recruitId}_${applicant?.uid}_3_750x750?alt=media&token=${time}`} alt='' />
              </td>
              <td style={{width: '260px', textAlign: 'left', fontSize: '12px', height: "20px", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B",}}>
                <p className={Styles.resume_hurigana_contents}>　{applicant?.coordinates?.coordinate3?.title ?? ""}</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '260px', textAlign: 'left', fontSize: '12px', height: "160px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", padding: "10px", borderBottom: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>{applicant?.coordinates?.coordinate3?.point ?? ""}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    : <></> }

    {documentData?.question ?
      <div className={Styles.box}>
        <table className={Styles.preview_screen_table_3}>
          <tbody>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>　{1 <= documentData?.questionCount && documentData?.questionCount <= 3 ? `${documentData?.question1}` : '' }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "160px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", padding: "10px" }}>
                <p className={Styles.resume_hurigana_contents}>{1 <= documentData?.questionCount && documentData?.questionCount <= 3 ? `${applicant?.questions?.question01 ?? ""}` : '' }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>　{2 <= documentData?.questionCount && documentData?.questionCount <= 3 ? `${documentData?.question2}` : '' }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "160px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", padding: "10px" }}>
                <p className={Styles.resume_hurigana_contents}>{2 <= documentData?.questionCount && documentData?.questionCount <= 3 ? `${applicant?.questions?.question02 ?? ""}` : '' }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>　{documentData?.questionCount === 3 ? `${documentData?.question3}` : '' }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "160px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", padding: "10px", borderBottom: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>{documentData?.questionCount === 3 ? `${applicant?.questions?.question03 ?? ""}` : '' }</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    : <></> }
    
    {documentData?.otherDocument ?
      <div className={Styles.box}>

        <table className={Styles.preview_screen_table_3}>
          <tbody>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>　{1 <= documentData?.otherDocumentCount && documentData?.otherDocumentCount <= 3 ? `${documentData?.otherDocument1 ?? ""}` : "" }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                {1 <= documentData?.otherDocumentCount && documentData?.otherDocumentCount <= 3 ?
                  <a href={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/otherDocuments%2F${applicant?.recruitId}_${applicant?.uid}_1?alt=media&token=${time}`} target="_blank" rel="noreferrer noopener">　プレビュー</a>
                : <></> }
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>　{2 <= documentData?.otherDocumentCount && documentData?.otherDocumentCount <= 3 ? `${documentData?.otherDocument2 ?? ""}` : "" }</p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                {2 <= documentData?.otherDocumentCount && documentData?.otherDocumentCount <= 3 ?
                  <a href={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/otherDocuments%2F${applicant?.recruitId}_${applicant?.uid}_2?alt=media&token=${time}`} target="_blank" rel="noreferrer noopener">　プレビュー</a>
                : <></> }
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B" }}>
                <p className={Styles.resume_hurigana_contents}>　{3 <= documentData?.otherDocumentCount && documentData?.otherDocumentCount <= 3 ? `${documentData?.otherDocument3 ?? ""}` : "" } </p>
              </td>
            </tr>
            <tr>
              <td style={{width: '410px', textAlign: 'left', fontSize: '12px', height: "20px", verticalAlign: 'top', borderTop: "1px solid #1B1B1B", borderLeft: "1px solid #1B1B1B", borderRight: "1px solid #1B1B1B", borderBottom: "1px solid #1B1B1B" }}>
                {documentData?.otherDocumentCount === 3 ?
                  <a href={`https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/otherDocuments%2F${applicant?.recruitId}_${applicant?.uid}_3?alt=media&token=${time}`} target="_blank" rel="noreferrer noopener">　プレビュー</a>
                : <></> }
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    : <></> }
    */}
    </>
  );
}

export default DetailsResume;


